import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Image, MapPin, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Realizations() {
  const { t, i18n } = useTranslation();
  const [realizations, setRealizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const lang = i18n.language;

  const categories = [
    { key: '', label: 'Tous' },
    { key: 'borne', label: 'Bornes Interactives' },
    { key: 'mur_led', label: 'Murs LED' },
    { key: 'televiseur', label: 'Téléviseurs' },
    { key: 'affichage', label: 'Affichage Dynamique' },
  ];

  useEffect(() => {
    fetchRealizations();
  }, [selectedCategory]);

  const fetchRealizations = async () => {
    try {
      setLoading(true);
      const params = selectedCategory ? `?category=${selectedCategory}` : '';
      const response = await axios.get(`${API}/realizations${params}`);
      setRealizations(response.data);
    } catch (error) {
      console.error('Error fetching realizations:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="realizations-page">
      {/* Header */}
      <section className="relative py-20 overflow-hidden bg-gray-50">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1694253987646-353954f1889d?w=1920"
            alt="Realizations"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white via-white/95 to-white" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center">
                <Image className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-black">{t('nav.realizations')}</h1>
            </div>
            <p className="text-gray-600 text-lg">
              Découvrez nos projets récents et comment nous avons transformé la communication visuelle de nos clients.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 border-y border-gray-200 bg-white sticky top-16 z-30">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Button
                key={cat.key}
                variant={selectedCategory === cat.key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(cat.key)}
                className={selectedCategory === cat.key ? 'bg-black text-white hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}
                data-testid={`filter-${cat.key || 'all'}`}
              >
                {cat.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Realizations Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="space-y-4">
                  <Skeleton className="aspect-video w-full bg-gray-100" />
                  <Skeleton className="h-4 w-3/4 bg-gray-100" />
                  <Skeleton className="h-4 w-1/2 bg-gray-100" />
                </div>
              ))}
            </div>
          ) : realizations.length === 0 ? (
            <div className="text-center py-12">
              <Image className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucune réalisation trouvée</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {realizations.map((realization, index) => (
                <motion.div
                  key={realization.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Card className="group bg-white border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300" data-testid={`realization-card-${realization.id}`}>
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={realization.images?.[0] || 'https://images.unsplash.com/photo-1694253987646-353954f1889d'}
                        alt={lang === 'fr' ? realization.title_fr : realization.title_en}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      {realization.is_featured && (
                        <Badge className="absolute top-3 left-3 bg-black text-white">
                          En vedette
                        </Badge>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1 font-mono">
                        {realization.client_name}
                      </p>
                      <h3 className="text-lg font-semibold text-black mb-2">
                        {lang === 'fr' ? realization.title_fr : realization.title_en}
                      </h3>
                      <p className="text-gray-500 text-sm mb-4 line-clamp-2">
                        {lang === 'fr' ? realization.description_fr : realization.description_en}
                      </p>
                      <div className="flex items-center gap-4 text-gray-400 text-sm">
                        {realization.location && (
                          <div className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            <span>{realization.location}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          <span>{realization.year}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
